/**
 * src/pages/admin/AdminSocial.jsx
 *
 * Control surface for the Instagram autoposter
 * (functions/modules/socialAutopost.js).
 *
 * Exists for one reason above the others: the autoposter publishes publicly
 * on a schedule with nobody watching, and before this page its off switch was
 * a field in a Firestore document. If a post lands badly you need to stop the
 * queue from your phone in seconds, not by navigating the Firebase console.
 *
 * Reads through callables rather than the client SDK because firestore.rules
 * denies config/** and socialPosts/** outright, which is correct: config
 * holds a live Meta access token.
 *
 * NOT here on purpose: approving content. That lives in
 * brand/social/calendar.mjs next to the copy it approves. Splitting them
 * would let you approve a post in a UI and then change what it says.
 */

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { usePlatformClaims } from '@/hooks/usePlatformClaims';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PauseCircleIcon,
  PlayCircleIcon,
} from '@heroicons/react/24/outline';

const fns = () => getFunctions(undefined, 'us-central1');

const STATUS_META = {
  posted:     { label: 'Posted',     cls: 'bg-lime-100 text-lime-800 border-lime-200' },
  queued:     { label: 'Queued',     cls: 'bg-zinc-100 text-zinc-600 border-zinc-200' },
  publishing: { label: 'Interrupted', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  failed:     { label: 'Failed',     cls: 'bg-red-100 text-red-700 border-red-200' },
};

function fmtDate(ms) {
  if (!ms) return '';
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function daysUntil(ms) {
  if (!ms) return null;
  return Math.round((ms - Date.now()) / 86400000);
}

export default function AdminSocial() {
  // Same shape as the other admin pages: AdminRoute handles the auth/loading
  // gate around the route, this only re-checks the founder claim.
  const { isFounder } = usePlatformClaims();
  const blocked = !isFounder;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toggling, setToggling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable(fns(), 'adminSocialStatus');
      const res = await fn();
      setData(res.data);
    } catch (e) {
      setError(e?.message || 'Could not load status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (!blocked) load(); }, [blocked, load]);

  async function toggle() {
    if (!data) return;
    const next = !data.enabled;
    // Turning it ON is the consequential direction: it starts public posting.
    if (next && !window.confirm(
      'Turn autoposting ON?\n\nApproved posts will publish to Instagram every Tuesday and Thursday at 9am with nobody reviewing them first.',
    )) return;

    setToggling(true);
    setError(null);
    try {
      const fn = httpsCallable(fns(), 'adminSetSocialEnabled');
      await fn({ enabled: next });
      await load();
    } catch (e) {
      setError(e?.message === 'not_configured'
        ? 'Not configured yet. Create config/instagram with a token first.'
        : (e?.message || 'Could not change the setting.'));
    } finally {
      setToggling(false);
    }
  }

  if (blocked) {
    return <div className="p-6 text-sm text-zinc-600">Founder access only.</div>;
  }

  const posts = data?.posts || [];
  const posted = posts.filter((p) => p.status === 'posted');
  const failed = posts.filter((p) => p.status === 'failed' || p.status === 'publishing');
  const queued = posts.filter((p) => p.status === 'queued');
  const expiryDays = daysUntil(data?.tokenExpiresAt);

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Social autoposting</h1>
          <p className="text-sm text-zinc-500">Instagram, @getscrimly</p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="p-2 rounded-lg hover:bg-zinc-100 disabled:opacity-40"
          aria-label="Refresh"
        >
          <ArrowPathIcon className={`w-5 h-5 text-zinc-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <ExclamationTriangleIcon className="w-5 h-5 flex-none" />
          <span>{error}</span>
        </div>
      )}

      {loading && !data && (
        <div className="rounded-lg border border-zinc-200 p-6 text-sm text-zinc-500">Loading…</div>
      )}

      {data && (
        <>
          {/* ── Kill switch ─────────────────────────────────────────── */}
          <div className={`rounded-xl border p-4 mb-4 ${
            data.enabled ? 'border-lime-300 bg-lime-50' : 'border-zinc-200 bg-zinc-50'
          }`}>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {data.enabled
                  ? <PlayCircleIcon className="w-8 h-8 text-lime-700 flex-none" />
                  : <PauseCircleIcon className="w-8 h-8 text-zinc-400 flex-none" />}
                <div>
                  <div className="font-semibold text-zinc-900">
                    {data.enabled ? 'Posting is ON' : 'Posting is OFF'}
                  </div>
                  <div className="text-sm text-zinc-600">
                    {data.enabled
                      ? 'Tuesdays and Thursdays, 9am Pacific.'
                      : data.configured
                        ? 'Nothing will publish until you turn this on.'
                        : 'Not configured yet.'}
                  </div>
                </div>
              </div>
              <button
                onClick={toggle}
                disabled={toggling || !data.configured}
                className={`px-4 py-2 rounded-lg font-semibold text-sm disabled:opacity-40 ${
                  data.enabled
                    ? 'bg-zinc-900 text-white hover:bg-zinc-800'
                    : 'bg-lime-400 text-zinc-900 hover:bg-lime-300'
                }`}
              >
                {toggling ? '…' : data.enabled ? 'Turn off' : 'Turn on'}
              </button>
            </div>
          </div>

          {/* ── Health ──────────────────────────────────────────────── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <Stat label="Posted" value={posted.length} />
            <Stat label="Remaining" value={queued.length} tone={queued.length === 0 ? 'warn' : undefined} />
            <Stat label="Failed" value={failed.length} tone={failed.length ? 'bad' : undefined} />
            <Stat
              label="Token"
              value={expiryDays === null ? '—' : `${expiryDays}d`}
              tone={expiryDays !== null && expiryDays < 14 ? 'bad' : undefined}
            />
          </div>

          {queued.length === 0 && data.manifestOk && (
            <Note>
              The queue is empty. Approve more entries in <code>brand/social/calendar.mjs</code>,
              then re-render and deploy <code>hosting:scrimly-social</code>.
            </Note>
          )}
          {!data.manifestOk && (
            <Note bad>
              Could not read the published calendar. The autoposter will not post until
              <code> hosting:scrimly-social</code> serves calendar.json.
            </Note>
          )}
          {data.configured && !data.hasAppCredentials && (
            <Note bad>
              No app id/secret stored, so the monthly token refresh cannot run. Posting will
              stop when this token expires.
            </Note>
          )}

          {/* ── Queue ───────────────────────────────────────────────── */}
          <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-500 mb-2">
            Queue
          </h2>
          <div className="rounded-xl border border-zinc-200 divide-y divide-zinc-100 overflow-hidden">
            {posts.length === 0 && (
              <div className="p-4 text-sm text-zinc-500">Nothing approved yet.</div>
            )}
            {posts.map((p) => {
              const meta = STATUS_META[p.status] || STATUS_META.queued;
              return (
                <div key={p.key} className="flex items-center gap-3 p-3">
                  <span className={`text-xs px-2 py-0.5 rounded border font-medium flex-none ${meta.cls}`}>
                    {meta.label}
                  </span>
                  <span className="text-sm text-zinc-900 truncate flex-1">
                    {p.key}
                    <span className="ml-2 text-xs text-zinc-400 uppercase">{p.kind}</span>
                  </span>
                  {p.postedAt && (
                    <span className="text-xs text-zinc-400 flex-none">{fmtDate(p.postedAt)}</span>
                  )}
                  {p.permalink && (
                    <a
                      href={p.permalink}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-lime-700 hover:underline flex-none"
                    >
                      View
                    </a>
                  )}
                </div>
              );
            })}
          </div>

          {failed.length > 0 && (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3">
              <div className="text-sm font-semibold text-red-800 mb-1">Needs attention</div>
              {failed.map((p) => (
                <div key={p.key} className="text-xs text-red-700">
                  {p.key}: {p.error || 'interrupted before publishing, skipped to avoid a duplicate'}
                </div>
              ))}
            </div>
          )}

          <p className="mt-6 text-xs text-zinc-400">
            Approving content happens in <code>brand/social/calendar.mjs</code>, next to the copy
            it approves. This page controls whether the queue runs, not what is in it.
          </p>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, tone }) {
  const toneCls = tone === 'bad'
    ? 'text-red-700'
    : tone === 'warn' ? 'text-amber-700' : 'text-zinc-900';
  return (
    <div className="rounded-lg border border-zinc-200 p-3">
      <div className="text-xs uppercase tracking-wide text-zinc-400">{label}</div>
      <div className={`text-xl font-bold ${toneCls}`}>{value}</div>
    </div>
  );
}

function Note({ children, bad }) {
  return (
    <div className={`mb-4 flex items-start gap-2 rounded-lg border p-3 text-sm ${
      bad ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-zinc-200 bg-zinc-50 text-zinc-600'
    }`}>
      {bad
        ? <ExclamationTriangleIcon className="w-5 h-5 flex-none" />
        : <CheckCircleIcon className="w-5 h-5 flex-none text-zinc-400" />}
      <span>{children}</span>
    </div>
  );
}
